import { SearchTypeahead } from '@bambi/component-library/src/components/SearchTypeahead/SearchTypeahead';

import { useAppSelector } from 'src/app/store/hooks';
import { usePaginationState } from 'src/common/DataGrid/usePaginationState';
import type {
  LocationsGeolocationsListApiArg} from 'src/common/external/bambi-api/bambiApi';
import {
  useLocationsGeolocationsListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { DEFAULT_PAGINATION_PAGE_SIZE } from 'src/common/external/bambi-api/constants';
import { useAutofocusOnVisible } from 'src/common/hooks/useAutofocusOnVisible';
import { useDebouncedSearch } from 'src/common/useDebouncedSearch';
import {
  selectGeolocationSearchTerm,
  selectGeolocationSourceFilter,
  setGeolocationSearchTerm,
} from 'src/features/settings/setting.slice';

interface GeolocationSearchProps {
  shouldAutoFocus?: boolean;
}

export function GeolocationSearch({ shouldAutoFocus }: GeolocationSearchProps) {
  const inputRef = useAutofocusOnVisible<HTMLInputElement>({
    shouldAutoFocus,
    selectText: true,
  });

  const searchTerm = useAppSelector(selectGeolocationSearchTerm);
  const { inputValue, handleInputChange } = useDebouncedSearch(
    setGeolocationSearchTerm,
    searchTerm
  );

  return (
    <SearchTypeahead
      value={inputValue}
      onChange={handleInputChange}
      placeholder="Search geolocations..."
      dataTestId="geolocation-search"
      ref={inputRef}
    />
  );
}

interface PaginatedGeolocationSearchResultsOptions {
  enableArchived: boolean;
}

/**
 * @param enableArchived - Whether to observe showArchivedGeolocations in state
 */
export const usePaginatedGeolocationSearchResults = (
  options: PaginatedGeolocationSearchResultsOptions
) => {
  const searchTerm = useAppSelector(selectGeolocationSearchTerm);
  const sourceFilter = useAppSelector(selectGeolocationSourceFilter);
  const showArchivedGeolocations = useAppSelector(
    (state) => state.setting.showArchivedGeolocations
  );
  const includeArchived = options.enableArchived && showArchivedGeolocations;

  const pageSize = DEFAULT_PAGINATION_PAGE_SIZE;
  const { pagination, handlePaginationChange, paramsWithPage } =
    usePaginationState<LocationsGeolocationsListApiArg>(
      {
        search: searchTerm,
        deletedObjects: includeArchived ? 'include' : undefined,
        sourceIn: sourceFilter.length > 0 ? sourceFilter : undefined,
      },
      {
        pageSize,
      }
    );
  const geolocationsListQueryState = useLocationsGeolocationsListQuery(
    {
      ...paramsWithPage,
      pageSize,
    },
    {
      selectFromResult: (result) => {
        return {
          ...result,
          geolocations: result.data?.results ?? [],
          totalCount: result.data?.count ?? 0,
        };
      },
    }
  );

  return {
    geolocationsListQueryState,
    searchIsActive: !!searchTerm,
    pagination,
    handlePaginationChange,
  };
};
